import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Bot, Zap, Eye, Shield, BarChart2, Inbox,
  Play, Square, RefreshCw, Clock, DollarSign, Activity,
} from 'lucide-react'
import { Card, MetricCard, Badge, PageHeader } from '../components/Card'
import { agentsStatus, agentLogs, startAgent, stopAgent } from '../api/client'
import { layout } from '../utils/layout'

const AGENT_META: Record<string, {
  label: string; color: string; icon: typeof Zap; capabilities: string[]
}> = {
  strategist: {
    label: 'Strategist',
    color: 'text-indigo-300',
    icon: Bot,
    capabilities: ['Strategic planning', 'Campaign direction', 'Cross-channel priorities'],
  },
  trendscout: {
    label: 'Trend Scout',
    color: 'text-cyan-300',
    icon: BarChart2,
    capabilities: ['Signal discovery', 'Trend scoring', 'Opportunity alerts'],
  },
  creator: {
    label: 'Creator',
    color: 'text-purple-400',
    icon: Zap,
    capabilities: ['Content generation', 'Platform adaptation', 'Creative ideation'],
  },
  editor: {
    label: 'Editor',
    color: 'text-emerald-300',
    icon: Eye,
    capabilities: ['Quality review', 'Tone checks', 'Copy refinement'],
  },
  publisher: {
    label: 'Publisher',
    color: 'text-blue-300',
    icon: Play,
    capabilities: ['Scheduling', 'Publish orchestration', 'Delivery tracking'],
  },
  communitymanager: {
    label: 'Community Manager',
    color: 'text-sky-300',
    icon: Inbox,
    capabilities: ['Inbox triage', 'Sentiment handling', 'Reply suggestions'],
  },
  adsoperator: {
    label: 'Ads Operator',
    color: 'text-orange-300',
    icon: Shield,
    capabilities: ['Budget optimization', 'Spend anomaly detection', 'ROAS monitoring'],
  },
  analyst: {
    label: 'Analyst',
    color: 'text-green-300',
    icon: BarChart2,
    capabilities: ['Performance analysis', 'KPI tracking', 'Executive summaries'],
  },
  growthhacker: {
    label: 'Growth Hacker',
    color: 'text-fuchsia-300',
    icon: Activity,
    capabilities: ['Experimentation', 'Conversion lift ideas', 'Growth playbooks'],
  },
  crisismanager: {
    label: 'Crisis Manager',
    color: 'text-red-400',
    icon: Shield,
    capabilities: ['Risk detection', 'Escalation control', 'Incident mitigation'],
  },
}

function statusColor(status: string): 'green' | 'yellow' | 'slate' | 'red' {
  if (status === 'running') return 'green'
  if (status === 'idle') return 'slate'
  if (status === 'error') return 'red'
  return 'yellow'
}

interface NormalizedFleetInfo {
  status: string
  tasks_today: number
  last_run: string | null
}

function normalizeAgentKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function normalizeFleetItem(entry: unknown): { agentKey: string; info: NormalizedFleetInfo } {
  const raw = (entry ?? {}) as Record<string, unknown>
  const nestedInfo = raw.info && typeof raw.info === 'object'
    ? (raw.info as Record<string, unknown>)
    : null

  const agentTypeRaw = typeof raw.agent_type === 'string'
    ? raw.agent_type
    : typeof raw.agentType === 'string'
      ? raw.agentType
      : 'unknown'

  const statusRaw = nestedInfo?.status ?? raw.status
  const tasksRaw = nestedInfo?.tasks_today ?? raw.tasks_today ?? raw.last_run_tasks
  const lastRunRaw = nestedInfo?.last_run ?? raw.last_run ?? raw.last_run_at

  const status = typeof statusRaw === 'string' ? statusRaw : 'unknown'
  const tasks_today = typeof tasksRaw === 'number'
    ? tasksRaw
    : Number(tasksRaw ?? 0) || 0
  const last_run = typeof lastRunRaw === 'string' ? lastRunRaw : null

  return {
    agentKey: normalizeAgentKey(agentTypeRaw),
    info: { status, tasks_today, last_run },
  }
}

export default function AgentsView() {
  const qc = useQueryClient()
  const [selectedLog, setSelectedLog] = useState<string | null>(null)

  const { data: fleet, isLoading } = useQuery({
    queryKey: ['agents'],
    queryFn: agentsStatus,
    refetchInterval: 10_000,
  })

  const { data: logs } = useQuery({
    queryKey: ['agent-logs'],
    queryFn: () => agentLogs(30),
    refetchInterval: 15_000,
  })

  const startMut = useMutation({
    mutationFn: (type: string) => startAgent(type),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents'] }),
  })

  const stopMut = useMutation({
    mutationFn: (type: string) => stopAgent(type),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents'] }),
  })

  const normalizedFleet = ((fleet?.fleet ?? []) as unknown[]).map(normalizeFleetItem)
  const totalCost = (logs?.logs ?? []).reduce((s, l) => s + (l.cost_usd ?? 0), 0)
  const totalTasks = (logs?.logs ?? []).reduce((s, l) => s + (l.tasks_completed ?? 0), 0)
  const runningCount = normalizedFleet.filter(f => f.info.status === 'running').length
  const errorCount = (logs?.logs ?? []).filter(l => l.status === 'error').length
  const fleetMap = Object.fromEntries(normalizedFleet.map(f => [f.agentKey, f.info]))

  return (
    <div>
      <PageHeader
        title="Agent Operations"
        subtitle="Autonomous agents · Live status · Cost tracking · Activity log"
        action={
          <button
            onClick={() => { qc.invalidateQueries({ queryKey: ['agents'] }); qc.invalidateQueries({ queryKey: ['agent-logs'] }) }}
            className="flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        }
      />

      <div className={`${layout.pagePadding} ${layout.pageStack}`}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard label="Running Agents" value={runningCount.toString()} />
          <MetricCard label="Tasks (logged)" value={totalTasks.toLocaleString()} />
          <MetricCard label="Total Cost" value={`$${totalCost.toFixed(4)}`} />
          <MetricCard label="Recent Errors" value={errorCount.toString()} />
        </div>

        <Card title="Autonomy Mode">
          <div className="flex items-start gap-3">
            <Bot className="w-5 h-5 text-brand-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-slate-200 mb-0.5">Copilot Mode Active</p>
              <p className="text-sm text-slate-400">Agents assist and suggest — all actions require human approval.</p>
              <p className="text-xs text-slate-500 mt-2">
                Change mode: <code className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-300">socialscale config set autonomy_mode autopilot</code>
              </p>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Object.entries(AGENT_META).map(([type, meta]) => {
            const info = fleetMap[type] ?? { status: 'idle', tasks_today: 0, last_run: null }
            const Icon = meta.icon
            const isPending = startMut.isPending || stopMut.isPending
            return (
              <Card key={type}>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-slate-800 rounded-lg shrink-0">
                    <Icon className={`w-5 h-5 ${meta.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-slate-200">{meta.label}</span>
                      <Badge color={isLoading ? 'slate' : statusColor(info.status)}>{info.status}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {meta.capabilities.map(cap => (
                        <span key={cap} className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full">{cap}</span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Activity className="w-3 h-3" /> {info.tasks_today ?? 0} today</span>
                        {info.last_run && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(info.last_run).toLocaleTimeString()}</span>}
                      </div>
                      {info.status !== 'running' ? (
                        <button onClick={() => startMut.mutate(type)} disabled={isPending}
                          className="flex items-center gap-1 text-xs bg-green-700/40 hover:bg-green-700/60 text-green-300 px-2 py-1 rounded-md">
                          <Play className="w-3 h-3" /> Start
                        </button>
                      ) : (
                        <button onClick={() => stopMut.mutate(type)} disabled={isPending}
                          className="flex items-center gap-1 text-xs bg-red-700/40 hover:bg-red-700/60 text-red-300 px-2 py-1 rounded-md">
                          <Square className="w-3 h-3" /> Stop
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>

        <Card title="Recent Activity Log" action={<span className="text-xs text-slate-500">{logs?.total ?? 0} entries</span>}>
          {!logs?.logs.length ? (
            <div className="py-6 text-center text-slate-500 text-sm">No agent activity yet.</div>
          ) : (
            <div className="divide-y divide-surface-border">
              {logs.logs.slice(0, 20).map(log => (
                <div key={log.id} onClick={() => setSelectedLog(l => l === log.id ? null : log.id)}
                  className="py-2.5 px-1 cursor-pointer hover:bg-slate-800/30 rounded transition-colors">
                  <div className="flex items-center gap-3 text-sm">
                    <Badge color={log.status === 'success' ? 'green' : log.status === 'error' ? 'red' : 'yellow'}>{log.status}</Badge>
                    <span className="text-slate-300 capitalize">{log.agent_type}</span>
                    <span className="text-slate-500 text-xs ml-auto">{log.tasks_completed} tasks</span>
                    <span className="flex items-center gap-0.5 text-xs text-slate-500"><DollarSign className="w-3 h-3" />{(log.cost_usd ?? 0).toFixed(4)}</span>
                    {log.completed_at && <span className="text-xs text-slate-600">{new Date(log.completed_at).toLocaleTimeString()}</span>}
                  </div>
                  {selectedLog === log.id && log.error && (
                    <div className="mt-2 p-2 bg-red-900/20 border border-red-900/30 rounded-lg">
                      <p className="text-xs text-red-400 font-mono">{log.error}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="MCP Server">
          <div className="space-y-2 text-sm text-slate-400">
            <p>Exposes SocialScale to Claude Desktop, Cursor, and other AI clients.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="bg-slate-800 rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">Stdio (Claude Desktop):</p><code className="text-xs text-slate-300">socialscale-mcp</code></div>
              <div className="bg-slate-800 rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">HTTP/SSE mode:</p><code className="text-xs text-slate-300">socialscale-mcp --port 7337</code></div>
            </div>
            <p className="text-xs text-slate-500">21 tools · 12 resources · 8 prompts · Protocol 2024-11-05</p>
          </div>
        </Card>
      </div>
    </div>
  )
}
